import { Injectable, inject, DestroyRef, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, interval, fromEvent, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from './auth.service';

/**
 * Session status with remaining time
 */
export interface SessionStatus {
  isValid: boolean;
  isExpiringSoon: boolean; // Less than 5 minutes
  isCritical: boolean; // Less than 2 minutes
  remainingMinutes: number;
  expirationTime: Date | null;
}

type SessionCheckTrigger = 'startup' | 'periodic' | 'activity' | 'manual';

/**
 * Session Monitor Service
 * 
 * Monitors JWT token expiration and provides proactive warnings
 * 
 * Features:
 * - Checks token expiration every 30 seconds
 * - Refreshes proactively shortly before expiry when the user is still active
 * - Shows progressive warnings (5min, 2min, 1min before expiration)
 * - Allows session refresh
 * - Automatic cleanup on expiration
 * 
 * @example
 * ```typescript
 * // In app.component.ts
 * constructor(private sessionMonitor: SessionMonitorService) {
 *   this.sessionMonitor.startMonitoring();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class SessionMonitorService {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);

  // The access token lifetime is defined server-side (observed ~90 min in dev).
  // A 30s check keeps the remaining-time heartbeat responsive and lets the
  // proactive refresh fire promptly once the threshold is crossed.
  private readonly CHECK_INTERVAL_MS = 30 * 1000;

  // Refresh well before expiry so an active user never hits save/submit with a
  // dead token. With a long-lived (~90 min) token this fires once, ~10 min before
  // expiry, then not again until 10 min before the NEXT expiry (no refresh storms).
  private readonly PROACTIVE_REFRESH_THRESHOLD_SEC = 10 * 60; // 600s

  // Consider the user "active" only if there was recent real interaction.
  private readonly RECENT_ACTIVITY_WINDOW_MS = 2 * 60 * 1000;

  // Avoid retrying proactive refresh too aggressively when the session cookie is gone.
  private readonly REFRESH_RETRY_COOLDOWN_MS = 45 * 1000;

  // Inactivity logout: log out after this many ms with no user interaction
  private readonly INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours

  // Warning thresholds kept only for critical fallback (no proactive refresh available)
  private readonly WARNING_5MIN = 5;
  private readonly WARNING_2MIN = 2;
  private readonly WARNING_1MIN = 1;

  private warningsShown = {
    fiveMin: false,
    twoMin: false,
    oneMin: false,
  };

  private lastActivityTime = Date.now();
  private lastRefreshAttemptTime = 0;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private monitoringStarted = false;
  private pendingExpiredSessionNotice = false;

  private sessionStatusSubject = new BehaviorSubject<SessionStatus>({
    isValid: false,
    isExpiringSoon: false,
    isCritical: false,
    remainingMinutes: 0,
    expirationTime: null,
  });

  public sessionStatus$ = this.sessionStatusSubject.asObservable();

  /**
   * Start monitoring the session
   * Should be called once in AppComponent
   */
  startMonitoring(): void {
    if (this.monitoringStarted) {
      return;
    }

    this.monitoringStarted = true;
    console.log('🔍 Session monitoring started');

    // Track user activity to reset inactivity timer
    this.ngZone.runOutsideAngular(() => {
      merge(
        fromEvent(document, 'keydown'),
        fromEvent(document, 'input'),
        fromEvent(document, 'click'),
        fromEvent(document, 'touchstart'),
        fromEvent(window, 'focus'),
      )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.lastActivityTime = Date.now();
          this.resetInactivityTimer();
          this.checkSession('activity');
        });

      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (document.visibilityState === 'visible') {
            this.lastActivityTime = Date.now();
            this.checkSession('activity');
          }
        });
    });

    this.resetInactivityTimer();

    // Initial check
    this.checkSession('startup');

    // Periodic checks every 30 seconds
    interval(this.CHECK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkSession('periodic');
      });
  }

  /** Restart the inactivity logout countdown */
  private resetInactivityTimer(): void {
    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer);
    }
    this.inactivityTimer = setTimeout(() => {
      console.warn('⚠️ [SessionMonitor] User inactive for 8 hours — logging out');
      this.messageService.add({
        severity: 'info',
        summary: 'Sessione scaduta',
        detail: 'Sei rimasto inattivo per 8 ore. Effettua nuovamente il login.',
        life: 6000,
      });
      setTimeout(() => this.logout(), 2000);
    }, this.INACTIVITY_TIMEOUT_MS);
  }

  /**
   * Check current session status
   */
  private checkSession(trigger: SessionCheckTrigger): void {
    const token = this.getToken();

    if (!token) {
      this.pendingExpiredSessionNotice = false;
      this.updateStatus({
        isValid: false,
        isExpiringSoon: false,
        isCritical: false,
        remainingMinutes: 0,
        expirationTime: null,
      });
      return;
    }

    try {
      const payload = this.decodeToken(token);
      
      if (!payload.exp) {
        console.warn('⚠️ Token has no expiration time');
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const remainingSeconds = payload.exp - now;
      const remainingMinutes = Math.floor(remainingSeconds / 60);
      const expirationTime = new Date(payload.exp * 1000);
      const userRecentlyActive = this.isUserRecentlyActive();
      const shouldHandleExpiredNow = trigger === 'activity' || trigger === 'manual' || userRecentlyActive;

      // If the token expired while the user was inactive, defer the user-facing
      // handling until the first real activity after their return.
      if (remainingSeconds <= 0) {
        if (!shouldHandleExpiredNow) {
          this.pendingExpiredSessionNotice = true;
          this.updateStatus({
            isValid: false,
            isExpiringSoon: false,
            isCritical: true,
            remainingMinutes: 0,
            expirationTime,
          });
          return;
        }

        console.warn('⚠️ [SessionMonitor] Token already expired — attempting silent refresh');
        this.pendingExpiredSessionNotice = false;
        this.lastRefreshAttemptTime = Date.now();
        this.authService.trySilentRefresh().then(newToken => {
          if (newToken) {
            console.log('✅ [SessionMonitor] Silent refresh succeeded after expiry');
            this.resetWarnings();
            this.checkSession('periodic');
          } else {
            console.warn('⚠️ [SessionMonitor] Silent refresh failed — session no longer recoverable');
            this.handleExpiredSession();
          }
        });
        return;
      }

      // Proactive refresh: token is still valid but expiring soon
      // Only refresh if the user has been active recently (avoid pointless refreshes for idle tabs)
      if (remainingSeconds <= this.PROACTIVE_REFRESH_THRESHOLD_SEC) {
        const idleMs = Date.now() - this.lastActivityTime;
        const userRecentlyActive = idleMs <= this.RECENT_ACTIVITY_WINDOW_MS;
        const canRetryRefresh = Date.now() - this.lastRefreshAttemptTime >= this.REFRESH_RETRY_COOLDOWN_MS;

        if (userRecentlyActive && canRetryRefresh) {
          console.log(`🔄 [SessionMonitor] Token expiring in ${remainingSeconds}s — proactive silent refresh`);
          this.lastRefreshAttemptTime = Date.now();
          this.authService.trySilentRefresh().then(newToken => {
            if (newToken) {
              this.resetWarnings();
              this.checkSession('periodic');
            }
          });
          return; // status will be updated on the next interval tick with the new token
        }
      }

      // Update status
      const status: SessionStatus = {
        isValid: true,
        isExpiringSoon: remainingMinutes <= this.WARNING_5MIN,
        isCritical: remainingMinutes <= this.WARNING_2MIN,
        remainingMinutes,
        expirationTime,
      };

      this.updateStatus(status);

      // Show warnings only when proactive refresh is unavailable (e.g. refresh_token also expired)
      this.showWarningIfNeeded(remainingMinutes);

    } catch (error) {
      console.error('❌ Error checking session:', error);
    }
  }

  /**
   * Show warning toast at specific thresholds
   */
  private showWarningIfNeeded(remainingMinutes: number): void {
    // 5 minutes warning
    if (remainingMinutes <= this.WARNING_5MIN && remainingMinutes > this.WARNING_2MIN && !this.warningsShown.fiveMin) {
      this.warningsShown.fiveMin = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Sessione in Scadenza',
        detail: `La tua sessione scadrà tra ${remainingMinutes} minuti. Salva i tuoi dati.`,
        life: 8000,
        sticky: false,
      });
    }

    // 2 minutes warning
    if (remainingMinutes <= this.WARNING_2MIN && remainingMinutes > this.WARNING_1MIN && !this.warningsShown.twoMin) {
      this.warningsShown.twoMin = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Sessione in Scadenza',
        detail: `La tua sessione scadrà tra ${remainingMinutes} minuti. Clicca qui per estendere la sessione.`,
        life: 10000,
        sticky: true,
        key: 'session-warning',
        data: { action: 'refresh' },
      });
    }

    // 1 minute critical warning
    if (remainingMinutes <= this.WARNING_1MIN && !this.warningsShown.oneMin) {
      this.warningsShown.oneMin = true;
      this.messageService.add({
        severity: 'error',
        summary: 'Sessione in Scadenza Imminente',
        detail: `La tua sessione scadrà tra ${remainingMinutes} minuto. Salva subito i tuoi dati!`,
        life: 15000,
        sticky: true,
        key: 'session-critical',
      });
    }
  }

  /**
   * Handle expired session
   */
  private handleExpiredSession(): void {
    this.updateStatus({
      isValid: false,
      isExpiringSoon: false,
      isCritical: false,
      remainingMinutes: 0,
      expirationTime: null,
    });

    // Clear warnings
    this.resetWarnings();

    // Show expiration message
    this.messageService.add({
      severity: 'error',
      summary: 'Sessione Scaduta',
      detail: 'La tua sessione è scaduta. Verrai reindirizzato al login.',
      life: 5000,
    });

    // Trigger logout after delay
    setTimeout(() => {
      void this.authService.startInteractiveReauthentication(
        this.router.url,
        'token_expired',
        'Sessione scaduta. Effettua nuovamente il login.'
      );
    }, 2000);
  }

  /**
   * Refresh/extend session
   * In production, this would call a refresh token endpoint
   */
  refreshSession(): void {
    console.log('🔄 Refreshing session...');

    this.lastRefreshAttemptTime = Date.now();
    void this.authService.trySilentRefresh().then(newToken => {
      if (newToken) {
        this.resetWarnings();
        this.checkSession('manual');
        this.messageService.add({
          severity: 'info',
          summary: 'Sessione Estesa',
          detail: 'La tua sessione è stata estesa con successo.',
          life: 4000,
        });
        return;
      }

      this.handleExpiredSession();
    });
  }

  /**
   * Manual logout
   */
  logout(): void {
    this.resetWarnings();
    this.authService.logout();
  }

  /**
   * Get current session status
   */
  getCurrentStatus(): SessionStatus {
    return this.sessionStatusSubject.value;
  }

  /**
   * Reset warning flags (used after refresh)
   */
  private resetWarnings(): void {
    this.warningsShown = {
      fiveMin: false,
      twoMin: false,
      oneMin: false,
    };
  }

  /**
   * Update session status
   */
  private updateStatus(status: SessionStatus): void {
    this.sessionStatusSubject.next(status);
  }

  private isUserRecentlyActive(): boolean {
    return document.visibilityState === 'visible'
      && Date.now() - this.lastActivityTime <= this.RECENT_ACTIVITY_WINDOW_MS;
  }

  /**
   * Get token from AuthService (in-memory only, never from localStorage).
   */
  private getToken(): string | null {
    return this.authService.getAccessToken();
  }

  /**
   * Decode JWT token
   */
  private decodeToken(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  }
}
